import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine
} from "recharts";
/* eslint-disable react-hooks/exhaustive-deps */

const API = "https://mattrack-backend.onrender.com";

const MATERIAL_META = {
  steel:           { name: "Steel (HRC)",         group: "Ferrous Metals",                color: "#60a5fa", type: "live" },
  iron_ore:        { name: "Iron Ore",             group: "Ferrous Metals",                color: "#fb7185", type: "live" },
  stainless_steel: { name: "Stainless Steel",      group: "Ferrous Metals",                color: "#94a3b8", type: "static" },
  copper:          { name: "Copper",               group: "Non-Ferrous Metals",            color: "#f97316", type: "live" },
  aluminum:        { name: "Aluminum",             group: "Non-Ferrous Metals",            color: "#34d399", type: "live" },
  zinc:            { name: "Zinc",                 group: "Non-Ferrous Metals",            color: "#a78bfa", type: "live" },
  nickel:          { name: "Nickel",               group: "Non-Ferrous Metals",            color: "#22d3ee", type: "static" },
  lead:            { name: "Lead",                 group: "Non-Ferrous Metals",            color: "#84cc16", type: "static" },
  tin:             { name: "Tin",                  group: "Non-Ferrous Metals",            color: "#f43f5e", type: "static" },
  brass:           { name: "Brass",                group: "Non-Ferrous Metals",            color: "#fbbf24", type: "static" },
  bronze:          { name: "Bronze",               group: "Non-Ferrous Metals",            color: "#f97316", type: "static" },
  gold:            { name: "Gold",                 group: "Precious Metals",               color: "#fbbf24", type: "live" },
  silver:          { name: "Silver",               group: "Precious Metals",               color: "#e2e8f0", type: "live" },
  platinum:        { name: "Platinum",             group: "Precious Metals",               color: "#67e8f9", type: "live" },
  palladium:       { name: "Palladium",            group: "Precious Metals",               color: "#f0abfc", type: "live" },
  titanium_alloys: { name: "Titanium Alloys",      group: "Refractory & Specialty Metals", color: "#38bdf8", type: "static" },
  tungsten_alloys: { name: "Tungsten Alloys",      group: "Refractory & Specialty Metals", color: "#a3e635", type: "static" },
  hdpe:            { name: "HDPE",                 group: "Polymers & Plastics",           color: "#c084fc", type: "static" },
  pvc:             { name: "PVC",                  group: "Polymers & Plastics",           color: "#f472b6", type: "static" },
  pet:             { name: "PET",                  group: "Polymers & Plastics",           color: "#fb923c", type: "static" },
  polypropylene:   { name: "Polypropylene",        group: "Polymers & Plastics",           color: "#4ade80", type: "static" },
  peek:            { name: "PEEK",                 group: "Polymers & Plastics",           color: "#e879f9", type: "static" },
  pc:              { name: "Polycarbonate",        group: "Polymers & Plastics",           color: "#38bdf8", type: "static" },
  pe:              { name: "Polyethylene",         group: "Polymers & Plastics",           color: "#4ade80", type: "static" },
  ptfe:            { name: "PTFE",                 group: "Polymers & Plastics",           color: "#fb923c", type: "static" },
  abs:             { name: "ABS",                  group: "Polymers & Plastics",           color: "#f472b6", type: "static" },
  nylon_6:         { name: "Polyamide (Nylon 6)",  group: "Polymers & Plastics",           color: "#a78bfa", type: "static" },
  oil:             { name: "Oil (WTI)",            group: "Energy Commodities",            color: "#fde68a", type: "live" },
  natgas:          { name: "Natural Gas",          group: "Energy Commodities",            color: "#6ee7b7", type: "live" },
  coal:            { name: "Coal",                 group: "Energy Commodities",            color: "#9ca3af", type: "live" },
};

const LIVE_GROUPS = ["Ferrous Metals", "Non-Ferrous Metals", "Precious Metals", "Energy Commodities"];
const STATIC_GROUPS = ["Ferrous Metals", "Non-Ferrous Metals", "Refractory & Specialty Metals", "Polymers & Plastics"];

// ── QUANT SIGNALS ─────────────────────────────────────────────────────────────

function computeSignals(prices) {
  if (!prices || prices.length < 20) return null;
  const vals = prices.map(p => p.price_usd);
  const n = vals.length;

  // Momentum: 14-day rate of change
  const momentum14 = ((vals[n - 1] - vals[Math.max(0, n - 14)]) / vals[Math.max(0, n - 14)]) * 100;

  // Volatility: 30-day coefficient of variation
  const window30 = vals.slice(Math.max(0, n - 30));
  const mean30 = window30.reduce((a, b) => a + b, 0) / window30.length;
  const variance = window30.reduce((a, b) => a + Math.pow(b - mean30, 2), 0) / window30.length;
  const vol30 = Math.sqrt(variance) / mean30 * 100;

  // Mean reversion: z-score vs 90-day moving average
  const window90 = vals.slice(Math.max(0, n - 90));
  const mean90 = window90.reduce((a, b) => a + b, 0) / window90.length;
  const std90 = Math.sqrt(window90.reduce((a, b) => a + Math.pow(b - mean90, 2), 0) / window90.length);
  const zScore = std90 > 0 ? (vals[n - 1] - mean90) / std90 : 0;

  return { momentum14, vol30, zScore, mean90 };
}

function MomentumBadge({ value }) {
  if (value === null || value === undefined) return <span style={{ color: "#333", fontSize: 10 }}>—</span>;
  const abs = Math.abs(value);
  let label, color, bg;
  if (abs < 1)      { label = "FLAT";     color = "#6b7280"; bg = "#111"; }
  else if (value > 5)  { label = "▲ STRONG"; color = "#22c55e"; bg = "#0a1f0a"; }
  else if (value > 1)  { label = "▲ MILD";   color = "#86efac"; bg = "#0a1a0a"; }
  else if (value < -5) { label = "▼ STRONG"; color = "#ef4444"; bg = "#1f0a0a"; }
  else                 { label = "▼ MILD";   color = "#fca5a5"; bg = "#1a0a0a"; }
  return (
    <span style={{
      fontSize: 9, fontWeight: 800, letterSpacing: 1.5,
      padding: "3px 7px", borderRadius: 2,
      background: bg, color, border: `1px solid ${color}33`
    }}>{label}</span>
  );
}

function VolatilityDots({ value }) {
  if (value === null || value === undefined) return <span style={{ color: "#333", fontSize: 10 }}>—</span>;
  let level, color;
  if (value < 2)      { level = 1; color = "#22c55e"; }
  else if (value < 5) { level = 2; color = "#fbbf24"; }
  else                { level = 3; color = "#ef4444"; }
  const label = ["", "LOW", "MED", "HIGH"][level];
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
      {[1, 2, 3].map(i => (
        <span key={i} style={{
          width: 6, height: 6, borderRadius: "50%",
          background: i <= level ? color : "#1a1a1a",
          border: `1px solid ${i <= level ? color : "#333"}`
        }} />
      ))}
      <span style={{ fontSize: 9, color, fontWeight: 700, letterSpacing: 1, marginLeft: 2 }}>{label}</span>
    </span>
  );
}

function ZScoreBadge({ value }) {
  if (value === null || value === undefined) return <span style={{ color: "#333", fontSize: 10 }}>—</span>;
  let label, color;
  if (value > 2)       { label = "OVERBOUGHT"; color = "#ef4444"; }
  else if (value > 1)  { label = "EXTENDED";   color = "#fbbf24"; }
  else if (value < -2) { label = "OVERSOLD";   color = "#22c55e"; }
  else if (value < -1) { label = "DEPRESSED";  color = "#86efac"; }
  else                 { label = "NEUTRAL";    color = "#6b7280"; }
  return (
    <span style={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <span style={{ fontSize: 9, color, fontWeight: 800, letterSpacing: 1 }}>{label}</span>
      <span style={{ fontSize: 9, color: "#444" }}>{value >= 0 ? "+" : ""}{value.toFixed(2)}σ</span>
    </span>
  );
}

// ── STYLES ────────────────────────────────────────────────────────────────────

const S = {
  app: { minHeight: "100vh", background: "#080808", color: "#e8e8e8", fontFamily: "'Barlow Condensed', 'Arial Narrow', sans-serif" },
  header: {
    background: "#0d0d0d", borderBottom: "3px solid #1a1a1a", padding: "0 40px",
    display: "flex", alignItems: "stretch", justifyContent: "space-between",
    height: 64, position: "sticky", top: 0, zIndex: 100,
  },
  logo: { display: "flex", alignItems: "center", gap: 14, borderRight: "1px solid #1a1a1a", paddingRight: 24, marginRight: 24 },
  logoText: { fontSize: 22, fontWeight: 800, letterSpacing: 4, textTransform: "uppercase", color: "#ffffff" },
  logoSub: { fontSize: 10, letterSpacing: 3, color: "#444", textTransform: "uppercase", marginTop: 2 },
  nav: { display: "flex", alignItems: "center", gap: 2 },
  navBtn: (active) => ({
    background: active ? "#1a1a1a" : "transparent", border: "none",
    borderBottom: active ? "3px solid #ffffff" : "3px solid transparent",
    color: active ? "#ffffff" : "#555", padding: "0 20px", height: "100%", cursor: "pointer",
    fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", fontFamily: "inherit", transition: "all 0.15s",
  }),
  currencyToggle: { display: "flex", alignItems: "center", gap: 2, background: "#111", border: "1px solid #222", borderRadius: 4, padding: 3 },
  currBtn: (active) => ({
    background: active ? "#ffffff" : "transparent", color: active ? "#000000" : "#555",
    border: "none", padding: "5px 14px", borderRadius: 2, cursor: "pointer",
    fontSize: 12, fontWeight: 800, letterSpacing: 2, fontFamily: "inherit", transition: "all 0.15s",
  }),
  main: { maxWidth: 1200, margin: "0 auto", padding: "32px 40px" },
  sectionLabel: {
    fontSize: 11, fontWeight: 700, letterSpacing: 4, textTransform: "uppercase",
    color: "#333", marginBottom: 12, marginTop: 40, display: "flex", alignItems: "center", gap: 12,
  },
  sectionLine: { flex: 1, height: 1, background: "#1a1a1a" },
  table: { width: "100%", borderCollapse: "collapse", marginBottom: 4 },
  th: { textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: "#333", padding: "10px 16px", borderBottom: "1px solid #1a1a1a" },
  tr: (expanded, selected) => ({ borderBottom: "1px solid #111", background: selected ? "#111800" : expanded ? "#111" : "transparent", cursor: "pointer", transition: "background 0.1s" }),
  td: { padding: "14px 16px", fontSize: 14, fontWeight: 500 },
  expandedRow: { background: "#0d0d0d", borderBottom: "2px solid #1a1a1a" },
  badge: (type) => ({
    display: "inline-block", fontSize: 9, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase",
    padding: "3px 8px", borderRadius: 2,
    background: type === "live" ? "#0a2a0a" : "#1a1a2a",
    color: type === "live" ? "#22c55e" : "#6366f1",
    border: `1px solid ${type === "live" ? "#166534" : "#3730a3"}`,
  }),
  staticGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 2, marginBottom: 4, alignItems: "start" },
  staticCard: (color) => ({ background: "#0d0d0d", border: "1px solid #1a1a1a", borderLeft: `3px solid ${color}`, padding: "16px 20px", cursor: "default" }),
  compareBar: { position: "fixed", bottom: 0, left: 0, right: 0, background: "#0d0d0d", borderTop: "2px solid #222", padding: "14px 40px", display: "flex", alignItems: "center", gap: 16, zIndex: 200 },
};

// ── TOOLTIP ───────────────────────────────────────────────────────────────────

const CustomTooltip = ({ active, payload, label, currency }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#0d0d0d", border: "1px solid #222", padding: "10px 14px", fontSize: 12, fontFamily: "inherit" }}>
      <div style={{ color: "#444", marginBottom: 6, letterSpacing: 2, fontSize: 10 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, fontWeight: 700, letterSpacing: 1 }}>
          {p.name}: {currency} {Number(p.value).toFixed(4)}/kg
        </div>
      ))}
    </div>
  );
};

// ── EXPANDED CHART ────────────────────────────────────────────────────────────

function ExpandedChart({ materialId, data, currency }) {
  const meta = MATERIAL_META[materialId];
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    axios.get(`${API}/suppliers/${materialId}`)
      .then(res => setSuppliers(res.data.suppliers))
      .catch(() => setSuppliers([]));
  }, [materialId]);

  const priceKey = currency === "AUD" ? "price_aud" : "price_usd";
  const hist = data.historical || [];
  const signals = computeSignals(hist);
  const audRatio = hist.length > 0 ? (hist[hist.length - 1].price_aud / hist[hist.length - 1].price_usd) : 1;

  return (
    <div style={{ padding: "20px 24px 24px" }}>
      {/* Signal bar */}
      {signals && (
        <div style={{
          display: "flex", gap: 32, marginBottom: 20, padding: "14px 18px",
          background: "#060606", border: "1px solid #1a1a1a", borderRadius: 2, flexWrap: "wrap",
        }}>
          <div>
            <div style={{ fontSize: 9, color: "#333", letterSpacing: 2, marginBottom: 6 }}>14D MOMENTUM</div>
            <MomentumBadge value={signals.momentum14} />
            <div style={{ fontSize: 9, color: "#444", marginTop: 3 }}>{signals.momentum14 >= 0 ? "+" : ""}{signals.momentum14.toFixed(2)}%</div>
          </div>
          <div style={{ width: 1, background: "#1a1a1a" }} />
          <div>
            <div style={{ fontSize: 9, color: "#333", letterSpacing: 2, marginBottom: 6 }}>30D VOLATILITY</div>
            <VolatilityDots value={signals.vol30} />
            <div style={{ fontSize: 9, color: "#444", marginTop: 3 }}>{signals.vol30.toFixed(1)}% CV</div>
          </div>
          <div style={{ width: 1, background: "#1a1a1a" }} />
          <div>
            <div style={{ fontSize: 9, color: "#333", letterSpacing: 2, marginBottom: 6 }}>VS 90D AVG</div>
            <ZScoreBadge value={signals.zScore} />
          </div>
          <div style={{ width: 1, background: "#1a1a1a" }} />
          <div>
            <div style={{ fontSize: 9, color: "#333", letterSpacing: 2, marginBottom: 6 }}>90D AVG PRICE</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#e8e8e8" }}>
              {currency === "AUD" ? `A$${(signals.mean90 * audRatio).toFixed(4)}` : `$${signals.mean90.toFixed(4)}`}
            </div>
            <div style={{ fontSize: 9, color: "#444", marginTop: 1 }}>per kg</div>
          </div>
        </div>
      )}

      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={hist} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`g-${materialId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={meta.color} stopOpacity={0.15} />
              <stop offset="100%" stopColor={meta.color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="2 4" stroke="#151515" vertical={false} />
          <XAxis dataKey="date" tick={{ fill: "#333", fontSize: 10, fontFamily: "inherit" }}
            tickLine={false} tickFormatter={d => d.slice(5)} interval={14} />
          <YAxis tick={{ fill: "#333", fontSize: 10, fontFamily: "inherit" }}
            tickLine={false} axisLine={false} tickFormatter={v => v.toFixed(2)} width={65} />
          <Tooltip content={<CustomTooltip currency={currency} />} />
          {signals && (
            <ReferenceLine
              y={currency === "AUD" ? signals.mean90 * audRatio : signals.mean90}
              stroke="#333" strokeDasharray="4 4"
              label={{ value: "90D AVG", fill: "#333", fontSize: 9, position: "insideTopRight" }}
            />
          )}
          <Area type="monotone" dataKey={priceKey} stroke={meta.color} strokeWidth={1.5}
            fill={`url(#g-${materialId})`} dot={false} name={meta.name} />
        </AreaChart>
      </ResponsiveContainer>

      {suppliers.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 3, color: "#333", marginBottom: 12, textTransform: "uppercase" }}>
            Suppliers & Producers
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {suppliers.map((s, i) => (
              <a key={i} href={s.website} target="_blank" rel="noreferrer" style={{
                display: "flex", alignItems: "center", gap: 8,
                background: "#0d0d0d", border: "1px solid #1a1a1a",
                borderLeft: `3px solid ${s.au ? "#22c55e" : "#333"}`,
                padding: "8px 14px", textDecoration: "none",
              }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#e8e8e8", letterSpacing: 1 }}>{s.name}</div>
                  <div style={{ fontSize: 10, color: "#444", letterSpacing: 1, marginTop: 2 }}>
                    {s.type.toUpperCase()} · {s.country}
                    {s.au && <span style={{ color: "#22c55e", marginLeft: 6 }}>● AU</span>}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── COMPARISON CHART ──────────────────────────────────────────────────────────

function ComparisonChart({ selected, allData, currency }) {
  const priceKey = currency === "AUD" ? "price_aud" : "price_usd";
  const dateMap = {};
  selected.forEach(id => {
    const hist = allData[id]?.historical || [];
    hist.forEach(d => {
      if (!dateMap[d.date]) dateMap[d.date] = { date: d.date };
      dateMap[d.date][id] = d[priceKey];
    });
  });
  const merged = Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div style={{ padding: "32px 40px", maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ ...S.sectionLabel, marginTop: 0 }}>
        <span>Comparing {selected.length} Materials</span>
        <div style={S.sectionLine} />
        <span style={{ color: "#555", fontSize: 10 }}>{currency}/KG</span>
      </div>
      <ResponsiveContainer width="100%" height={340}>
        <LineChart data={merged} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="2 4" stroke="#151515" vertical={false} />
          <XAxis dataKey="date" tick={{ fill: "#333", fontSize: 10, fontFamily: "inherit" }}
            tickLine={false} tickFormatter={d => d.slice(5)} interval={14} />
          <YAxis tick={{ fill: "#333", fontSize: 10, fontFamily: "inherit" }}
            tickLine={false} axisLine={false} tickFormatter={v => v.toFixed(2)} width={65} />
          <Tooltip content={<CustomTooltip currency={currency} />} />
          {selected.map(id => (
            <Line key={id} type="monotone" dataKey={id}
              stroke={MATERIAL_META[id]?.color || "#fff"} strokeWidth={1.5} dot={false} name={MATERIAL_META[id]?.name || id} />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <div style={{ display: "flex", gap: 24, marginTop: 16, flexWrap: "wrap" }}>
        {selected.map(id => (
          <div key={id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 24, height: 2, background: MATERIAL_META[id]?.color }} />
            <span style={{ fontSize: 11, color: "#666", letterSpacing: 2, textTransform: "uppercase" }}>{MATERIAL_META[id]?.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── LIVE TABLE ────────────────────────────────────────────────────────────────

function LiveTable({ group, materials, allData, currency, expanded, setExpanded, selected, toggleSelect }) {
  const priceKey = currency === "AUD" ? "price_aud" : "price_usd";
  const symbol = currency === "AUD" ? "A$" : "$";

  return (
    <div style={{ marginBottom: 2 }}>
      <div style={S.sectionLabel}>
        <span>{group}</span>
        <div style={S.sectionLine} />
        <span style={{ color: "#1a1a1a", fontSize: 10 }}>LIVE</span>
      </div>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={{ ...S.th, width: 32 }} />
            <th style={S.th}>Material</th>
            <th style={S.th}>Current Price</th>
            <th style={S.th}>7D Change</th>
            <th style={S.th}>Momentum</th>
            <th style={S.th}>Volatility</th>
            <th style={S.th}>vs 90D Avg</th>
            <th style={{ ...S.th, width: 32 }} />
          </tr>
        </thead>
        <tbody>
          {materials.map(id => {
            const meta = MATERIAL_META[id];
            const data = allData[id];
            const hist = data?.historical || [];
            const latest = hist[hist.length - 1]?.[priceKey] || 0;
            const weekAgo = hist[hist.length - 8]?.[priceKey] || latest;
            const change = weekAgo ? ((latest - weekAgo) / weekAgo) * 100 : 0;
            const signals = computeSignals(hist);
            const isExpanded = expanded === id;
            const isSelected = selected.includes(id);

            return (
              <>
                <tr key={id}
                  style={S.tr(isExpanded, isSelected)}
                  onClick={() => setExpanded(isExpanded ? null : id)}
                  onMouseEnter={e => { if (!isExpanded && !isSelected) e.currentTarget.style.background = "#0f0f0f"; }}
                  onMouseLeave={e => { if (!isExpanded && !isSelected) e.currentTarget.style.background = "transparent"; }}
                >
                  <td style={{ ...S.td, paddingRight: 0 }} onClick={e => { e.stopPropagation(); toggleSelect(id); }}>
                    <div style={{
                      width: 16, height: 16, border: `2px solid ${isSelected ? meta.color : "#333"}`,
                      borderRadius: 2, background: isSelected ? meta.color : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center"
                    }}>
                      {isSelected && <span style={{ fontSize: 10, color: "#000", fontWeight: 900 }}>✓</span>}
                    </div>
                  </td>
                  <td style={S.td}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 3, height: 20, background: meta.color, borderRadius: 1 }} />
                      <span style={{ fontWeight: 700, letterSpacing: 1, fontSize: 15 }}>{meta.name}</span>
                    </div>
                  </td>
                  <td style={{ ...S.td, fontWeight: 800, fontSize: 16, letterSpacing: 1 }}>
                    {symbol}{latest.toFixed(4)}<span style={{ fontSize: 11, color: "#444", fontWeight: 400 }}>/kg</span>
                  </td>
                  <td style={{ ...S.td, color: change >= 0 ? "#22c55e" : "#ef4444", fontWeight: 700 }}>
                    {change >= 0 ? "▲" : "▼"} {Math.abs(change).toFixed(2)}%
                  </td>
                  <td style={S.td}><MomentumBadge value={signals?.momentum14} /></td>
                  <td style={S.td}><VolatilityDots value={signals?.vol30} /></td>
                  <td style={S.td}><ZScoreBadge value={signals?.zScore} /></td>
                  <td style={{ ...S.td, color: "#333", fontSize: 16 }}>{isExpanded ? "▲" : "▼"}</td>
                </tr>
                {isExpanded && (
                  <tr key={`${id}-exp`} style={S.expandedRow}>
                    <td colSpan={8} style={{ padding: 0 }}>
                      {data ? <ExpandedChart materialId={id} data={data} currency={currency} /> :
                        <div style={{ padding: 24, color: "#333" }}>Loading...</div>}
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── STATIC CARD ───────────────────────────────────────────────────────────────

function StaticCard({ id, staticData, currency }) {
  const meta = MATERIAL_META[id];
  const data = staticData[id];
  const symbol = currency === "AUD" ? "A$" : "$";
  const [isOpen, setIsOpen] = useState(false);
  const [suppliers, setSuppliers] = useState(null);

  if (!data) return null;

  const minPrice = currency === "AUD" ? data.price_min : data.price_min_usd;
  const maxPrice = currency === "AUD" ? data.price_max : data.price_max_usd;

  const toggleSuppliers = async () => {
    setIsOpen(prev => !prev);
    if (!isOpen && suppliers === null) {
      try {
        const res = await axios.get(`${API}/suppliers/${id}`);
        setSuppliers(res.data.suppliers);
      } catch { setSuppliers([]); }
    }
  };

  return (
    <div style={S.staticCard(meta.color)}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" }}>{meta.name}</span>
        <span style={S.badge("static")}>REF</span>
      </div>
      <div style={{ fontSize: 10, color: "#444", letterSpacing: 2, marginBottom: 4 }}>PRICE RANGE</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 4 }}>
        <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: 1 }}>{symbol}{minPrice?.toFixed(2)}</span>
        <span style={{ fontSize: 13, color: "#444" }}>—</span>
        <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: 1 }}>{symbol}{maxPrice?.toFixed(2)}</span>
        <span style={{ fontSize: 11, color: "#444", fontWeight: 400 }}>/kg</span>
      </div>
      <div style={{ fontSize: 10, color: "#333", letterSpacing: 1, marginTop: 8 }}>
        SOURCE: {data.source || "EduPack 2025"}<br />UPDATED: {data.updated || "—"}
      </div>
      <div onClick={toggleSuppliers} style={{
        marginTop: 12, paddingTop: 10, borderTop: "1px solid #1a1a1a",
        fontSize: 10, letterSpacing: 2, color: "#555", cursor: "pointer",
        userSelect: "none", display: "flex", justifyContent: "space-between"
      }}>
        <span>SUPPLIERS</span><span>{isOpen ? "▲" : "▼"}</span>
      </div>
      {isOpen && (
        <div style={{ marginTop: 8 }}>
          {suppliers === null ? (
            <div style={{ fontSize: 10, color: "#444" }}>Loading...</div>
          ) : suppliers.length === 0 ? (
            <div style={{ fontSize: 10, color: "#444" }}>No suppliers found</div>
          ) : (
            suppliers.map((s, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: "1px solid #111", fontSize: 10 }}>
                <div>
                  <span style={{ color: "#e0e0e0", fontWeight: 700 }}>{s.name}</span>
                  <span style={{ color: "#444", marginLeft: 6 }}>{s.type}</span>
                  <span style={{ color: "#333", marginLeft: 6 }}>{s.country}</span>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {s.au && <span style={{ fontSize: 9, background: "#0a2a0a", color: "#4ade80", border: "1px solid #166534", borderRadius: 3, padding: "1px 5px" }}>AU</span>}
                  <a href={s.website} target="_blank" rel="noreferrer" style={{ color: "#555", fontSize: 9, textDecoration: "none" }} onClick={e => e.stopPropagation()}>↗</a>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function StaticPanel({ group, materials, staticData, currency }) {
  return (
    <div style={{ marginBottom: 2 }}>
      <div style={S.sectionLabel}>
        <span>{group}</span><div style={S.sectionLine} />
        <span style={{ color: "#1a1a2a", fontSize: 10 }}>EDUPACK REF</span>
      </div>
      <div style={S.staticGrid}>
        {materials.map(id => <StaticCard key={id} id={id} staticData={staticData} currency={currency} />)}
      </div>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────────────

export default function App() {
  const [currency, setCurrency] = useState("USD");
  const [view, setView] = useState("prices");
  const [allData, setAllData] = useState({});
  const [staticData, setStaticData] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [selected, setSelected] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const LIVE_IDS = Object.entries(MATERIAL_META).filter(([, m]) => m.type === "live").map(([id]) => id);
  const STATIC_IDS = Object.entries(MATERIAL_META).filter(([, m]) => m.type === "static").map(([id]) => id);

  const fetchData = async () => {
    try {
      const liveResults = {};
      await Promise.all(LIVE_IDS.map(async id => {
        try {
          const res = await axios.get(`${API}/prices/${id}`);
          liveResults[id] = { historical: res.data.prices || [] };
        } catch {
          liveResults[id] = { historical: [] };
        }
      }));
      setAllData(liveResults);
      setLoading(false);

      const staticResults = {};
      await Promise.all(STATIC_IDS.map(async id => {
        try {
          const res = await axios.get(`${API}/prices/${id}`);
          if (res.data.type === "static") staticResults[id] = res.data.data;
        } catch {}
      }));
      setStaticData(staticResults);
      setLastUpdated(new Date());
    } catch (e) {
      console.error("Fetch error", e);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    timerRef.current = setInterval(fetchData, 60000);
    return () => clearInterval(timerRef.current);
  }, []);

  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const liveByGroup = LIVE_GROUPS.reduce((acc, g) => {
    acc[g] = Object.entries(MATERIAL_META).filter(([, m]) => m.group === g && m.type === "live").map(([id]) => id);
    return acc;
  }, {});

  const staticByGroup = STATIC_GROUPS.reduce((acc, g) => {
    acc[g] = Object.entries(MATERIAL_META).filter(([, m]) => m.group === g && m.type === "static").map(([id]) => id);
    return acc;
  }, {});

  return (
    <div style={S.app}>
      <title>IndusPrice</title>
      <link href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      <div style={S.header}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={S.logo}>
            <div style={{ width: 36, height: 36, background: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 18, fontWeight: 900, color: "#000", letterSpacing: -1 }}>I</span>
            </div>
            <div>
              <div style={S.logoText}>IndusPrice</div>
              <div style={S.logoSub}>Industrial Materials Price Intelligence</div>
            </div>
          </div>
          <div style={S.nav}>
            {[["prices", "Price Table"], ["compare", "Compare"]].map(([v, label]) => (
              <button key={v} style={S.navBtn(view === v)} onClick={() => setView(v)}>{label}</button>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          <div style={{ fontSize: 11, color: "#333", letterSpacing: 2 }}>
            <span style={{ color: "#22c55e" }}>●</span> LIVE &nbsp;{lastUpdated.toLocaleTimeString()}
          </div>
          <div style={S.currencyToggle}>
            {["USD", "AUD"].map(c => <button key={c} style={S.currBtn(currency === c)} onClick={() => setCurrency(c)}>{c}</button>)}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 80, color: "#333", letterSpacing: 4, fontSize: 12 }}>LOADING PRICES...</div>
      ) : view === "compare" ? (
        selected.length >= 2
          ? <ComparisonChart selected={selected} allData={allData} currency={currency} />
          : <div style={{ textAlign: "center", padding: 80, color: "#333", letterSpacing: 3, fontSize: 12 }}>SELECT 2+ MATERIALS FROM THE PRICE TABLE TO COMPARE</div>
      ) : (
        <div style={S.main}>
          {LIVE_GROUPS.map(group => (
            <LiveTable key={group} group={group} materials={liveByGroup[group]}
              allData={allData} currency={currency} expanded={expanded}
              setExpanded={setExpanded} selected={selected} toggleSelect={toggleSelect} />
          ))}
          <div style={{ ...S.sectionLabel, marginTop: 48 }}>
            <span>Reference Prices</span><div style={S.sectionLine} />
            <span style={{ color: "#333", fontSize: 10 }}>EDUPACK 2025 — UPDATE EACH SEMESTER</span>
          </div>
          {STATIC_GROUPS.map(group => (
            <StaticPanel key={group} group={group} materials={staticByGroup[group]} staticData={staticData} currency={currency} />
          ))}
        </div>
      )}

      {selected.length > 0 && (
        <div style={S.compareBar}>
          <span style={{ fontSize: 11, color: "#444", letterSpacing: 3, textTransform: "uppercase" }}>Comparing:</span>
          {selected.map(id => (
            <div key={id} style={{ display: "flex", alignItems: "center", gap: 8, background: "#111", border: `1px solid ${MATERIAL_META[id]?.color}33`, padding: "5px 12px", borderRadius: 2 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: MATERIAL_META[id]?.color }} />
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>{MATERIAL_META[id]?.name}</span>
              <span style={{ fontSize: 14, color: "#444", cursor: "pointer", marginLeft: 4 }} onClick={() => toggleSelect(id)}>×</span>
            </div>
          ))}
          {selected.length >= 2 && (
            <button onClick={() => setView("compare")} style={{ marginLeft: "auto", background: "#ffffff", color: "#000", border: "none", padding: "8px 20px", cursor: "pointer", fontSize: 12, fontWeight: 800, letterSpacing: 3, textTransform: "uppercase", fontFamily: "inherit" }}>Compare →</button>
          )}
          <button onClick={() => setSelected([])} style={{ background: "transparent", color: "#444", border: "1px solid #222", padding: "7px 14px", cursor: "pointer", fontSize: 11, fontWeight: 700, letterSpacing: 2, fontFamily: "inherit" }}>CLEAR</button>
        </div>
      )}
    </div>
  );
}